import { FC, useState } from 'react';
import { ExternalAccount, useExternalAccounts } from './useExternalAccounts';
import { Button, Flex, Table, theme, message } from 'antd';
import { upperCaseFirstLetter } from '../../libs/string';
import { css } from '@emotion/css';
import { CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CreateExternalAccountModal } from './CustomerPage.CreateExternalAccountModal';

const { Column } = Table;

export const ExternalAccountsView: FC<{ customerId: string }> = ({ customerId }) => {
  const query = useExternalAccounts(customerId);
  const { token } = theme.useToken();
  const [messageApi, contextHolder] = message.useMessage();
  const [showAddExternalAccountModal, setShowAddExternalAccountModal] = useState(false);

  return (
    <Flex gap="middle" vertical>
      {contextHolder}
      <Flex align="center" justify="flex-end" gap="middle">
        <Button
          type="primary"
          onClick={() => {
            setShowAddExternalAccountModal(true);
          }}
        >
          Add External Account
        </Button>
      </Flex>

      <Table<ExternalAccount> loading={query.isFetching || query.isPending} dataSource={query?.data?.items || []}>
        <Column
          title="Account Holder"
          key="holder"
          render={(_: any, record: ExternalAccount) => {
            let name = `${record.account_holder_first_name} ${record.account_holder_last_name}`;
            if (record.account_holder_business_name) {
              name = record.account_holder_business_name;
            }

            const subHeaderStyles = css`
              color: ${token.colorTextSecondary};
              font-size: 12px;
            `;
            return (
              <Flex vertical>
                <span>{name}</span>
                <span className={subHeaderStyles}>{upperCaseFirstLetter(record.account_holder_type)}</span>
              </Flex>
            );
          }}
        />

        <Column
          title="Account ID"
          key="name"
          render={(_: any, record: ExternalAccount) => {
            const subHeaderStyles = css`
              color: ${token.colorTextSecondary};
            `;
            return (
              <Flex align="center">
                {record.id.substring(0, 12)}...
                <Button
                  type="text"
                  className={subHeaderStyles}
                  size="small"
                  icon={<CopyOutlined width={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(record.id);
                    messageApi.success('ID copied to clipboard');
                  }}
                />
              </Flex>
            );
          }}
        />

        <Column
          title="Date Added"
          key="date_added"
          render={(_: any, record: ExternalAccount) => {
            return dayjs(record.created_at).format('MMM D, YYYY');
          }}
        />

        <Column
          title="Type"
          key="type"
          render={(_: any, record: ExternalAccount) => {
            return record.account_type.split('_').join(' ');
          }}
        />

        <Column
          title="Details"
          key="details"
          render={(_: any, record: ExternalAccount) => {
            const preStyles = css`
              color: ${token.colorTextTertiary};
            `;

            if (record.iban) {
              return (
                <>
                  <Flex vertical>
                    <div>
                      <span className={preStyles}>Bank: </span>
                      <span>{record.bank_name}</span>
                    </div>
                    <div>
                      <span className={preStyles}>IBAN: </span>
                      <span>{record.iban.iban_number}</span>
                    </div>

                    <div>
                      <span className={preStyles}>BIC: </span>
                      <span> {record.iban.bic}</span>
                    </div>
                  </Flex>
                </>
              );
            }

            if (record.international_wire) {
              return (
                <>
                  <Flex vertical>
                    <div>
                      <span className={preStyles}>Bank: </span>
                      <span>{record.bank_name}</span>
                    </div>
                    <div>
                      <span className={preStyles}>Account Number: </span>
                      <span>{record.international_wire.account_number}</span>
                    </div>

                    <div>
                      <span className={preStyles}>Routing Number: </span>
                      <span> {record.international_wire.routing_number}</span>
                    </div>

                    <div>
                      <span className={preStyles}>BIC: </span>
                      <span> {record.international_wire.bic}</span>
                    </div>
                  </Flex>
                </>
              );
            }

            if (record.us) {
              return (
                <>
                  <Flex vertical>
                    <div>
                      <span className={preStyles}>Bank: </span>
                      <span>{record.bank_name}</span>
                    </div>
                    <div>
                      <span className={preStyles}>Account Number: </span>
                      <span>{record.us.account_number}</span>
                    </div>

                    <div>
                      <span className={preStyles}>Routing Number: </span>
                      <span> {record.us.routing_number}</span>
                    </div>
                  </Flex>
                </>
              );
            }

            return null;
          }}
        />
      </Table>

      {showAddExternalAccountModal && (
        <CreateExternalAccountModal
          open={showAddExternalAccountModal}
          customerId={customerId}
          onClose={() => {
            setShowAddExternalAccountModal(false);
          }}
        />
      )}
    </Flex>
  );
};
